import { useMemo, useState } from "react";
import { Info, RotateCcw, ArrowRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import Reveal from "./Reveal";
import CountUp from "./CountUp";
import { trackEvent } from "../lib/analytics";

const DEFAULT_ROD_LENGTH = 12; // metres
const DIAMETERS = [8, 10, 12, 16, 20, 25, 32];

const DIAMETER_INFO = {
  8: "Commonly used for stirrups and ties.",
  10: "Slabs and residential structures.",
  12: "Beams and columns.",
  16: "Structural reinforcement.",
  20: "Heavy load-bearing structures.",
  25: "Industrial applications.",
  32: "Large infrastructure projects.",
};

const emptyRowState = () =>
  DIAMETERS.reduce((acc, d) => {
    acc[d] = { qty: "", weight: "", mode: null }; // mode: null | "rods" | "weight"
    return acc;
  }, {});

/**
 * Premium builder-friendly steel weight calculator (dual-input).
 *
 * For each diameter the user can enter either:
 *  – Qty (Rods) → Result column shows calculated weight in kg
 *  – Weight (KG) → Result column shows approximate rod count
 * Entering one auto-clears the other so there is no conflict to resolve.
 */
const SteelWeightCalculator = ({
  rodLength = DEFAULT_ROD_LENGTH,
  onEnquire,
}) => {
  const [rows, setRows] = useState(emptyRowState);
  const [hasFiredCalcUseEvent, setHasFiredCalcUseEvent] = useState(false);

  const sanitizeInt = (raw) => raw.replace(/[^\d]/g, "").slice(0, 7);
  const sanitizeDecimal = (raw) =>
    raw.replace(/[^\d.]/g, "").replace(/(\..*)\./g, "$1").slice(0, 9);

  const fireCalcUse = (mode, diameter) => {
    if (hasFiredCalcUseEvent) return;
    trackEvent("calculator_use", { mode, diameter });
    setHasFiredCalcUseEvent(true);
  };

  const setQty = (d, raw) => {
    const cleaned = sanitizeInt(raw);
    if (cleaned) fireCalcUse("rods", d);
    setRows((prev) => ({
      ...prev,
      [d]: {
        qty: cleaned,
        weight: cleaned === "" ? prev[d].weight : "",
        mode: cleaned === "" ? null : "rods",
      },
    }));
  };

  const setWeight = (d, raw) => {
    const cleaned = sanitizeDecimal(raw);
    if (cleaned) fireCalcUse("weight", d);
    setRows((prev) => ({
      ...prev,
      [d]: {
        weight: cleaned,
        qty: cleaned === "" ? prev[d].qty : "",
        mode: cleaned === "" ? null : "weight",
      },
    }));
  };

  const reset = () => {
    setRows(emptyRowState());
    setHasFiredCalcUseEvent(false);
  };

  const computed = useMemo(() => {
    const list = DIAMETERS.map((d) => {
      const weightPerRod = ((d * d) / 162) * rodLength;
      const row = rows[d];
      let qNum = 0;
      let wNum = 0;
      if (row.mode === "rods") {
        qNum = Math.max(0, Number(row.qty) || 0);
        wNum = weightPerRod * qNum;
      } else if (row.mode === "weight") {
        wNum = Math.max(0, Number(row.weight) || 0);
        qNum = weightPerRod > 0 ? Math.round(wNum / weightPerRod) : 0;
      }
      return {
        d,
        qty: qNum,
        weight: wNum,
        mode: row.mode,
        weightPerRod,
      };
    });
    const totalWeight = list.reduce((s, r) => s + r.weight, 0);
    const totalRods = list.reduce((s, r) => s + r.qty, 0);
    return {
      rows: list,
      totalWeight,
      totalTonnes: totalWeight / 1000,
      totalRods,
    };
  }, [rows, rodLength]);

  const hasInput = computed.totalWeight > 0 || computed.totalRods > 0;

  return (
    <TooltipProvider delayDuration={120}>
      <div
        data-testid="steel-weight-calculator"
        className="grid gap-6 md:grid-cols-12 md:gap-8"
      >
        {/* LEFT — input table */}
        <Reveal className="md:col-span-7">
          <div className="rounded-3xl border border-black/5 bg-white p-6 shadow-lux md:p-8">
            {/* Header */}
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
                  / Steel Weight Calculator
                </div>
                <h3 className="mt-3 font-display text-3xl uppercase leading-[0.95] tracking-tighter text-[#1c1c1e] md:text-4xl">
                  Calculate your<br />
                  <span className="text-[#e85a1c]">steel requirement.</span>
                </h3>
                <p className="mt-3 max-w-md text-[13px] leading-relaxed text-[#4a4a4e]">
                  Enter rods <em>or</em> weight in any row — the calculator
                  fills in the other side automatically.
                </p>
              </div>
              <button
                onClick={reset}
                data-testid="calc-reset"
                disabled={!hasInput}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#4a4a4e] transition-all hover:border-[#e85a1c] hover:text-[#e85a1c] disabled:opacity-40"
              >
                <RotateCcw size={11} /> Reset
              </button>
            </div>

            {/* Table header (desktop) */}
            <div className="mt-7 hidden grid-cols-12 gap-3 border-b border-black/5 pb-2 md:grid">
              <div className="col-span-2 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                Diameter
              </div>
              <div className="col-span-3 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                Qty (rods)
              </div>
              <div className="col-span-3 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                Weight (kg)
              </div>
              <div className="col-span-4 text-right font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
                Result
              </div>
            </div>

            {/* Diameter rows */}
            <div className="mt-1 divide-y divide-black/5">
              {computed.rows.map((r) => {
                const active = r.mode !== null && (r.qty > 0 || r.weight > 0);
                const rowState = rows[r.d];
                return (
                  <div
                    key={r.d}
                    data-testid={`calc-row-${r.d}`}
                    className={`grid grid-cols-1 gap-2 py-2.5 transition-colors md:grid-cols-12 md:items-center md:gap-3 ${
                      active ? "bg-[#fff8f3]/50" : ""
                    }`}
                  >
                    {/* Diameter */}
                    <div className="flex items-center gap-2 md:col-span-2">
                      <span
                        className={`font-display text-xl tracking-tighter md:text-2xl ${
                          active ? "text-[#e85a1c]" : "text-[#1c1c1e]"
                        }`}
                      >
                        {r.d}
                        <span className="ml-0.5 text-sm text-[#8b8b8f]">
                          mm
                        </span>
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            data-testid={`calc-info-${r.d}`}
                            aria-label={`Info about ${r.d} mm`}
                            className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-black/10 bg-white text-[#8b8b8f] transition-all hover:border-[#e85a1c] hover:text-[#e85a1c]"
                          >
                            <Info size={11} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent
                          side="top"
                          className="max-w-[220px] bg-[#1c1c1e] text-white"
                        >
                          <div className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#ffaa7a]">
                            {r.d} mm rebar
                          </div>
                          <div className="mt-1 text-xs leading-relaxed">
                            {DIAMETER_INFO[r.d]}
                          </div>
                          <div className="mt-2 border-t border-white/10 pt-2 font-mono-industrial text-[9px] uppercase tracking-[0.2em] text-white/60">
                            1 rod ≈ {r.weightPerRod.toFixed(2)} kg
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Mobile labels + Qty input */}
                    <div className="md:col-span-3">
                      <label className="mb-0.5 block font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f] md:hidden">
                        Qty (rods)
                      </label>
                      <div
                        className={`flex items-center overflow-hidden rounded-lg border bg-white transition-all ${
                          r.mode === "rods"
                            ? "border-[#e85a1c]/40 ring-2 ring-[#e85a1c]/10"
                            : "border-black/10"
                        } focus-within:border-[#e85a1c] focus-within:ring-2 focus-within:ring-[#e85a1c]/15`}
                      >
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={rowState.qty}
                          onChange={(e) => setQty(r.d, e.target.value)}
                          placeholder="0"
                          data-testid={`calc-qty-${r.d}`}
                          className="flex-1 bg-transparent px-3 py-1.5 font-mono-industrial text-sm focus:outline-none"
                        />
                        <span className="pr-3 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#bababd]">
                          rods
                        </span>
                      </div>
                    </div>

                    {/* Weight input */}
                    <div className="md:col-span-3">
                      <label className="mb-0.5 block font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f] md:hidden">
                        Or weight (kg)
                      </label>
                      <div
                        className={`flex items-center overflow-hidden rounded-lg border bg-white transition-all ${
                          r.mode === "weight"
                            ? "border-[#e85a1c]/40 ring-2 ring-[#e85a1c]/10"
                            : "border-black/10"
                        } focus-within:border-[#e85a1c] focus-within:ring-2 focus-within:ring-[#e85a1c]/15`}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          value={rowState.weight}
                          onChange={(e) => setWeight(r.d, e.target.value)}
                          placeholder="0"
                          data-testid={`calc-weightin-${r.d}`}
                          className="flex-1 bg-transparent px-3 py-1.5 font-mono-industrial text-sm focus:outline-none"
                        />
                        <span className="pr-3 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#bababd]">
                          kg
                        </span>
                      </div>
                    </div>

                    {/* Result */}
                    <div className="md:col-span-4 md:text-right">
                      <div
                        data-testid={`calc-result-${r.d}`}
                        className={`font-display text-base tracking-tight md:text-lg ${
                          active ? "text-[#1c1c1e]" : "text-[#bababd]"
                        }`}
                      >
                        {!active ? (
                          <span>—</span>
                        ) : r.mode === "rods" ? (
                          <CountUp
                            value={r.weight}
                            decimals={2}
                            suffix=" kg"
                          />
                        ) : (
                          <span>
                            <CountUp value={r.qty} decimals={0} />
                            <span className="ml-1 text-sm text-[#8b8b8f]">
                              rods
                            </span>
                          </span>
                        )}
                      </div>
                      {active && (
                        <div className="font-mono-industrial text-[9px] uppercase tracking-[0.2em] text-[#bababd] md:mt-0.5">
                          {r.mode === "rods" ? "from rods" : "≈ approx."}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <p className="mt-5 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-[#8b8b8f]">
              Standard {rodLength}-metre rods · (D² ÷ 162) × length × qty
            </p>
          </div>
        </Reveal>

        {/* RIGHT — sticky summary panel */}
        <div className="md:col-span-5">
          <div className="md:sticky md:top-28">
            <Reveal delay={100}>
              <div
                data-testid="calc-grand-total"
                className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#1c1c1e] via-[#2a2a2c] to-[#1c1c1e] p-7 text-white shadow-2xl md:p-8"
              >
                <div className="absolute inset-0 bg-grid opacity-10" />
                <div className="absolute -right-24 top-1/2 h-64 w-64 -translate-y-1/2 rounded-full bg-[#e85a1c]/30 blur-3xl" />
                <div className="relative">
                  <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#ffaa7a]">
                    / Total steel required
                  </div>

                  {/* Hero — total kg */}
                  <div className="mt-5">
                    <div className="font-mono-industrial text-[9px] uppercase tracking-[0.3em] text-white/50">
                      Total Weight
                    </div>
                    <div className="mt-1 font-display text-[52px] uppercase leading-[0.9] tracking-tighter text-[#e85a1c] md:text-[64px]">
                      <CountUp
                        value={computed.totalWeight}
                        decimals={1}
                        testId="calc-total-weight"
                      />
                      <span className="ml-2 text-xl text-white/70 md:text-2xl">
                        kg
                      </span>
                    </div>
                  </div>

                  {/* Sub stats */}
                  <div className="mt-6 grid grid-cols-2 gap-3 border-t border-white/10 pt-5">
                    <div>
                      <div className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-white/50">
                        Tonnage
                      </div>
                      <div className="mt-1 font-display text-2xl tracking-tighter text-white">
                        <CountUp
                          value={computed.totalTonnes}
                          decimals={3}
                          testId="calc-total-tonnes"
                          suffix=" T"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-white/50">
                        Total Rods
                      </div>
                      <div className="mt-1 font-display text-2xl tracking-tighter text-white">
                        <CountUp
                          value={computed.totalRods}
                          decimals={0}
                          testId="calc-total-rods"
                        />
                      </div>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent("quote_click", {
                        source: "calculator",
                        total_weight_kg: Math.round(computed.totalWeight),
                        total_rods: computed.totalRods,
                      });
                      if (onEnquire) {
                        onEnquire(computed);
                      } else {
                        window.location.href = "/#enquiry";
                      }
                    }}
                    data-testid="calc-enquire"
                    disabled={!hasInput}
                    className="group mt-6 inline-flex w-full items-center justify-between gap-3 rounded-full bg-[#e85a1c] px-5 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all hover:bg-[#c64812] disabled:opacity-40"
                  >
                    <span>Send Enquiry</span>
                    <ArrowRight
                      size={14}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </button>

                  {!hasInput && (
                    <p className="mt-4 font-mono-industrial text-[9px] uppercase tracking-[0.25em] text-white/40">
                      Enter quantities or weights to see your total live.
                    </p>
                  )}
                </div>
              </div>

              <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#8b8b8f]">
                Indicative weight only. Final billing basis calibrated weighbridge.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </TooltipProvider>
  );
};

export default SteelWeightCalculator;
